/**
 * useWebPush — orchestrates the browser-side push subscription lifecycle.
 *
 * Exposes a tiny state machine the Settings page can render:
 *   'unsupported' — SW or push API missing (e.g. Firefox Focus, old iOS).
 *   'server-off'  — server has no VAPID keys; push feature hidden.
 *   'unregistered'— SW not registered yet (still loading).
 *   'denied'      — user denied the OS permission prompt.
 *   'off'         — SW ready, permission granted (or default), no sub.
 *   'on'          — we have an active subscription known to the server.
 *
 * The hook is cheap to call from multiple components — it reads the live
 * registration each render rather than caching in module scope so a user
 * who clears site data gets an accurate status next mount.
 */
import { useCallback, useEffect, useState } from 'react';
import { pushApi } from '../services/pushApi';

export type PushStatus =
  | 'unsupported'
  | 'server-off'
  | 'unregistered'
  | 'denied'
  | 'off'
  | 'on';

// Decode the base64-URL VAPID public key the browser wants in the
// `applicationServerKey` field of pushManager.subscribe. The VAPID spec
// ships keys as base64url, but PushManager expects a BufferSource.
//
// We allocate an explicit ArrayBuffer (not SharedArrayBuffer) and return
// the underlying buffer — recent TS lib types made `Uint8Array<ArrayBufferLike>`
// *not* structurally assignable to `BufferSource` because the generic's
// buffer could be a SharedArrayBuffer. Handing back a raw ArrayBuffer
// sidesteps the narrowing entirely.
function urlBase64ToArrayBuffer(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; ++i) view[i] = raw.charCodeAt(i);
  return buffer;
}

function isSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function useWebPush() {
  const [status, setStatus] = useState<PushStatus>('unregistered');
  const [vapidKey, setVapidKey] = useState<string | null>(null);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Initial boot — check support, fetch VAPID key, register the SW, and
  // surface any existing subscription. Runs once; the cleanup does nothing
  // because the SW should outlive the React component.
  useEffect(() => {
    let cancelled = false;

    if (!isSupported()) {
      setStatus('unsupported');
      return;
    }

    (async () => {
      try {
        const key = await pushApi.getVapidPublicKey();
        if (cancelled) return;
        if (!key) {
          setStatus('server-off');
          return;
        }
        setVapidKey(key);

        // Register the SW. `updateViaCache: 'none'` forces the browser to
        // re-check sw.js on each navigation so push-payload bug fixes
        // propagate quickly.
        const reg = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
        if (cancelled) return;
        setRegistration(reg);

        const existing = await reg.pushManager.getSubscription();
        if (cancelled) return;
        setSubscription(existing);

        const perm = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
        if (perm === 'denied') setStatus('denied');
        else setStatus(existing ? 'on' : 'off');
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message);
        setStatus('off');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    if (!registration || !vapidKey) return;
    setBusy(true);
    setError(null);
    try {
      // Ask for permission up front. If the user blocks, we don't retry
      // silently — they have to unblock via browser site settings.
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setStatus('denied');
        return;
      }

      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true, // Chrome/Edge require this
        applicationServerKey: urlBase64ToArrayBuffer(vapidKey),
      });

      await pushApi.subscribe(sub, navigator.userAgent);
      setSubscription(sub);
      setStatus('on');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [registration, vapidKey]);

  const disable = useCallback(async () => {
    if (!subscription) return;
    setBusy(true);
    setError(null);
    try {
      // Call the server *before* we unsubscribe locally so a failed server
      // call still leaves us in a known state (we can retry). The race
      // window where the server has the sub but the browser doesn't is
      // harmless — next push just prunes it via 410.
      try {
        await pushApi.unsubscribe(subscription.endpoint);
      } catch {
        // Best-effort — proceed to browser-side unsubscribe anyway so
        // the user doesn't get stuck in a half-on state.
      }
      await subscription.unsubscribe();
      setSubscription(null);
      setStatus('off');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [subscription]);

  return { status, error, busy, enable, disable };
}
